/**
 * ChildEventTab — one child event's tab on its parent's Event Detail page.
 *
 * Re-reads the child with GetEvent rather than rendering the copy ListSubEvents returned: a listing
 * returns the description's file references unsigned (AWSECEventHub api-integration-guide), so its
 * images would not load; GetEvent signs them. Then renders the same cards as the child's own page
 * (EventDetailBody), which loads the child's attendees and hierarchy itself.
 *
 * Mounted only once its tab is first opened (the page's Tabs are lazy), so a parent with many
 * children does not read them all up front.
 */

import { useEffect, useState } from 'react';
import { Alert, Box, StatusIndicator } from '@amzn/awsui-components-react';
import { eventHubCalendarClient, type Event } from '../../client/EventCalendarClient';
import EventDetailBody, { type EventDetailBodyProps } from './EventDetailBody';

export interface ChildEventTabProps {
  eventId: string;
  /** The page's event, which is this child's parent — shown as its Parent Event without a re-read. */
  parent: { eventId: string; eventName?: string };
  roles: EventDetailBodyProps['roles'];
  login: string;
}

export default function ChildEventTab({ eventId, parent, roles, login }: ChildEventTabProps) {
  const [event, setEvent] = useState<Event | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setEvent(null);
    setError(null);
    eventHubCalendarClient()
      .getEvent(eventId, { signal: controller.signal })
      .then(setEvent, (e: unknown) => {
        if (!controller.signal.aborted) setError(e instanceof Error ? e.message : String(e));
      });
    return () => controller.abort();
  }, [eventId]);

  if (error) {
    return (
      <Box padding={{ top: 'l' }}>
        <Alert type="error" header="Failed to load this child event">
          {error}
        </Alert>
      </Box>
    );
  }
  if (!event) {
    return (
      <Box padding={{ top: 'l' }}>
        <StatusIndicator type="loading">Loading</StatusIndicator>
      </Box>
    );
  }
  return (
    <EventDetailBody
      event={event}
      roles={roles}
      login={login}
      knownParent={parent}
      pageOverlay={false}
    />
  );
}
